'use client';

import { useInfiniteQuery, useQuery } from '@tanstack/react-query';

import {
  getABTestByIdService,
  getABTestListService,
} from '@/services/httpServices/ABTestServices';
import type {
  ABTestListResponse,
  ABTestListServiceConfig,
  GetABTestByIdServiceConfig,
} from '@/services/httpServices/ABTestServices/types';

import { abTestByIdKey, abTestListKeys, infiniteAbTestListKeys } from './abTestQueryKeys';
import { buildStandardPaginatedParams } from './createInfiniteQuery';
import { useQueryConfig } from './useQueryConfig';

export function useABTestListQuery(params: ABTestListServiceConfig, enabled: boolean = true) {
  const { isEnabled } = useQueryConfig();

  return useQuery({
    queryKey: abTestListKeys(params),
    queryFn: async () => getABTestListService(params),
    enabled: isEnabled && enabled,
  });
}

/**
 * Hook to fetch AB test list with infinite scrolling using cursor-based pagination
 * @param initialParams - Initial AB test list query parameters
 * @param enabled - Whether to enable the query
 * @returns Infinite query result for the AB test list
 */
export function useInfiniteABTestListQuery(
  initialParams: ABTestListServiceConfig,
  enabled: boolean = true,
) {
  const { isEnabled } = useQueryConfig();

  return useInfiniteQuery<ABTestListResponse>({
    queryKey: infiniteAbTestListKeys(initialParams),
    queryFn: async ({ pageParam }) => {
      const paginationParams = buildStandardPaginatedParams(
        initialParams,
        pageParam ? String(pageParam) : undefined,
      );
      return getABTestListService(paginationParams);
    },
    initialPageParam: '',
    getNextPageParam: (lastPage) => {
      if (lastPage.pageInfo.hasNextPage === true && lastPage.pageInfo.endCursor !== null) {
        return lastPage.pageInfo.endCursor;
      }
      return undefined;
    },
    getPreviousPageParam: (firstPage) => {
      if (firstPage.pageInfo.hasPreviousPage === true && firstPage.pageInfo.startCursor !== null) {
        return firstPage.pageInfo.startCursor;
      }
      return undefined;
    },
    enabled: isEnabled && enabled,
  });
}

export function useABTestByIdQuery(params: GetABTestByIdServiceConfig) {
  const { isEnabled } = useQueryConfig();

  return useQuery({
    queryKey: abTestByIdKey(params),
    queryFn: async () => getABTestByIdService(params),
    enabled: isEnabled
      && params.params?.id !== undefined
      && params.params?.id !== null
      && params.params?.id !== '',
  });
}
